import { useRef, useState } from 'react';
import {
  CorrectionStrategy,
  ValidationRuleStatus,
  ValidationRuleTrigger,
  ValidationRuleType,
} from '@erp/shared-types';
import {
  DEFAULT_VALIDATION_NORMALIZATION,
  type CatalogAdvancedFilter,
  type PreviewValidationRuleResponse,
  type ValidationNormalization,
  type ValidationRuleDefinitionInput,
  type ValidationRuleDTO,
  type ValidationRuleSchemaDTO,
} from '@erp/rpc';
import { useAllDataSourceLinks } from '@/api/rpc/hooks/core/useDataSources';
import {
  useCreateValidationRule,
  usePreviewValidationRule,
  useUpdateValidationRule,
  useValidationRuleSchemas,
} from '@/api/rpc/hooks/inventory-management/useValidationRules';
import {
  fieldKey,
  rowFiltersForSave,
  ruleDefinitionKey,
  scheduleCronForSave,
  toggleFieldInOrder,
} from './validationRuleWizardChecks';

/** One division the selected schema is stored in, as the strategy step offers it. */
export interface WizardDivisionOption {
  /** `core_data_sources.id` — what `preferredDataSourceId` holds. */
  id: string;
  title: string;
}

/** Which of the three normalization flags a toggle owns. */
export type NormalizationFlag = keyof ValidationNormalization;

/**
 * The rule builder's state: the rule being authored, the schema metadata it is
 * authored against, and the two side effects it runs — previewing, and saving.
 *
 * Everything that *decides* something lives in the tested pure checks
 * (`validationRuleWizardChecks`); this hook only holds state and calls the API.
 * State is seeded once from `rule`, so the dialog mounts the wizard fresh per
 * open (create) or per rule (edit) rather than syncing props into state.
 */
export function useValidationRuleWizardState(options: {
  /** The rule being edited, or null when authoring a new one. */
  rule: ValidationRuleDTO | null;
  /** Called with the saved rule's id once the save lands. */
  onSaved: (ruleId: string) => void;
}) {
  const { rule, onSaved } = options;
  const mode: 'create' | 'edit' = rule ? 'edit' : 'create';

  const {
    schemas,
    isLoading: schemasLoading,
    error: schemasError,
  } = useValidationRuleSchemas();
  const { data: linkData } = useAllDataSourceLinks();

  const [schemaName, setSchemaName] = useState(rule?.schemaName ?? '');
  const [rowFilters, setRowFilters] = useState<CatalogAdvancedFilter[]>(
    rule?.rowFilters ?? [],
  );
  const [groupingFields, setGroupingFields] = useState<string[]>(
    rule?.groupingFields ?? [],
  );
  const [validatedFields, setValidatedFields] = useState<string[]>(
    rule?.validatedFields ?? [],
  );
  const [normalization, setNormalization] = useState<ValidationNormalization>(
    rule?.normalization ?? { ...DEFAULT_VALIDATION_NORMALIZATION },
  );
  const [correctionStrategy, setCorrectionStrategy] =
    useState<CorrectionStrategy>(
      rule?.correctionStrategy ?? CorrectionStrategy.MostCommon,
    );
  const [preferredDataSourceId, setPreferredDataSourceId] = useState(
    rule?.strategyConfig?.preferredDataSourceId ?? '',
  );
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(
    rule?.assigneeUserId ?? null,
  );
  const [executionTrigger, setExecutionTrigger] =
    useState<ValidationRuleTrigger>(
      rule?.executionTrigger ?? ValidationRuleTrigger.Manual,
    );
  const [scheduleCron, setScheduleCron] = useState(rule?.scheduleCron ?? '');
  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [activateOnSave, setActivateOnSave] = useState(
    rule?.status === ValidationRuleStatus.Active,
  );

  const [preview, setPreview] = useState<PreviewValidationRuleResponse | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewedKey, setPreviewedKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const previewRule = usePreviewValidationRule();
  const createRule = useCreateValidationRule();
  const updateRule = useUpdateValidationRule();

  const selectedSchema: ValidationRuleSchemaDTO | null =
    schemas.find((schema) => schema.name === schemaName) ?? null;
  const schemaFieldNames = (selectedSchema?.fields ?? []).map(
    (field) => field.fieldName,
  );

  /**
   * The divisions the org holds the selected schema in — the same set the
   * server validates `preferredDataSourceId` against, derived from the schema's
   * data-source links so the picker can only offer a division that will pass.
   */
  const divisions: WizardDivisionOption[] = [];
  const seenDivisionIds = new Set<string>();
  for (const group of linkData?.groups ?? []) {
    for (const link of group.links) {
      if (link.schemaName !== schemaName) continue;
      if (seenDivisionIds.has(link.dataSourceId)) continue;
      seenDivisionIds.add(link.dataSourceId);
      divisions.push({ id: link.dataSourceId, title: group.sourceTitle });
    }
  }

  /** Picking a different table invalidates everything named in terms of it. */
  function selectSchema(next: string) {
    if (next === schemaName) return;
    setSchemaName(next);
    setRowFilters([]);
    setGroupingFields([]);
    setValidatedFields([]);
    setPreferredDataSourceId('');
    clearPreview();
  }

  /**
   * The id of the newest preview request. Bumped by every `runPreview` and by
   * `clearPreview`, so a response whose request is no longer the newest is
   * discarded instead of overwriting the current preview or a cleared one.
   */
  const previewRequestSequence = useRef(0);

  function clearPreview() {
    previewRequestSequence.current += 1;
    setPreview(null);
    setPreviewError(null);
    setPreviewedKey(null);
  }

  const definition: ValidationRuleDefinitionInput = {
    schemaName,
    rowFilters: rowFiltersForSave(rowFilters),
    groupingFields,
    validatedFields,
    normalization,
    correctionStrategy,
    strategyConfig:
      correctionStrategy === CorrectionStrategy.PreferredSource
        ? { preferredDataSourceId }
        : null,
  };
  const definitionKey = ruleDefinitionKey(definition);

  /**
   * Evaluate the unsaved definition. A failure carries the server's message —
   * verbatim, since a definition error names the author's own fields — RETURNED
   * as well as stored: an awaiting caller holds the state snapshot from before
   * the await, where `previewError` is still the previous value.
   *
   * A response that is no longer the newest request (a later preview started,
   * or `clearPreview` ran) is discarded without touching state; it reports
   * `ok: false` with no message, since whatever superseded it owns the outcome.
   */
  async function runPreview(): Promise<{
    ok: boolean;
    message: string | null;
  }> {
    const requestSequence = ++previewRequestSequence.current;
    setPreviewError(null);
    try {
      const result = await previewRule.mutateAsync({ rule: definition });
      if (requestSequence !== previewRequestSequence.current) {
        return { ok: false, message: null };
      }
      setPreview(result);
      setPreviewedKey(definitionKey);
      return { ok: true, message: null };
    } catch (error) {
      if (requestSequence !== previewRequestSequence.current) {
        return { ok: false, message: null };
      }
      const message = errorMessage(error);
      setPreview(null);
      setPreviewedKey(definitionKey);
      setPreviewError(message);
      return { ok: false, message };
    }
  }

  /**
   * Whether the save step may choose the rule's status. A draft (and anything
   * being created) starts as one or goes live; an already-live or paused rule
   * keeps the status it has — activating and pausing belong to the rule page.
   */
  const canChooseStatus =
    mode === 'create' || rule?.status === ValidationRuleStatus.Draft;

  async function handleComplete(): Promise<void> {
    setSaveError(null);
    const status = activateOnSave
      ? ValidationRuleStatus.Active
      : ValidationRuleStatus.Draft;

    try {
      if (rule) {
        const result = await updateRule.mutateAsync({
          id: rule.id,
          name: name.trim(),
          description: description.trim() || null,
          schemaName,
          rowFilters: definition.rowFilters,
          groupingFields,
          validatedFields,
          normalization,
          correctionStrategy,
          strategyConfig: definition.strategyConfig,
          assigneeUserId,
          executionTrigger,
          scheduleCron: scheduleCronForSave(executionTrigger, scheduleCron),
          ...(canChooseStatus ? { status } : {}),
        });
        onSaved(result.rule.id);
        return;
      }

      const result = await createRule.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        schemaName,
        ruleType: ValidationRuleType.FieldConsistency,
        rowFilters: definition.rowFilters,
        groupingFields,
        validatedFields,
        normalization,
        correctionStrategy,
        strategyConfig: definition.strategyConfig,
        assigneeUserId,
        status,
        executionTrigger,
        scheduleCron: scheduleCronForSave(executionTrigger, scheduleCron),
      });
      onSaved(result.rule.id);
    } catch (error) {
      setSaveError(errorMessage(error));
    }
  }

  return {
    mode,
    ruleId: rule?.id ?? null,

    schemas,
    schemasLoading,
    schemasError: schemasError ?? null,
    selectedSchema,
    schemaName,
    selectSchema,

    rowFilters,
    setRowFilters: (filters: CatalogAdvancedFilter[]) => {
      setRowFilters(filters);
      clearPreview();
    },

    groupingFields,
    toggleGroupingField: (field: string) => {
      setGroupingFields((current) =>
        toggleFieldInOrder(current, field, schemaFieldNames),
      );
      clearPreview();
    },

    validatedFields,
    toggleValidatedField: (field: string) => {
      setValidatedFields((current) =>
        toggleFieldInOrder(current, field, schemaFieldNames),
      );
      clearPreview();
    },
    /** The validated fields that are also grouping keys, for the inline error. */
    overlappingFields: validatedFields.filter((field) =>
      groupingFields.some((grouped) => fieldKey(grouped) === fieldKey(field)),
    ),

    normalization,
    setNormalizationFlag: (flag: NormalizationFlag, value: boolean) => {
      setNormalization((current) => ({ ...current, [flag]: value }));
      clearPreview();
    },

    correctionStrategy,
    setCorrectionStrategy: (strategy: CorrectionStrategy) => {
      setCorrectionStrategy(strategy);
      clearPreview();
    },
    preferredDataSourceId,
    setPreferredDataSourceId: (id: string) => {
      setPreferredDataSourceId(id);
      clearPreview();
    },
    divisions,

    assigneeUserId,
    setAssigneeUserId,
    executionTrigger,
    setExecutionTrigger,
    scheduleCron,
    setScheduleCron,

    name,
    setName,
    description,
    setDescription,
    activateOnSave,
    setActivateOnSave,
    canChooseStatus,

    definition,
    definitionKey,
    preview,
    previewError,
    previewedKey,
    previewing: previewRule.isPending,
    runPreview,

    saveError,
    saving: createRule.isPending || updateRule.isPending,
    handleComplete,
    busy: previewRule.isPending || createRule.isPending || updateRule.isPending,
  };
}

export type ValidationRuleWizardState = ReturnType<
  typeof useValidationRuleWizardState
>;

/** A thrown value as the message to show. oRPC surfaces 400s as `Error`s. */
function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
